import { config } from "../../config/env";
import { IPolicyAdminProvider } from "./interfaces/IPolicyAdminProvider";
import { RISEProvider } from "./providers/RISEProvider";
import { SurefyreProvider } from "./providers/SurefyreProvider";
import { Application, ApplicationDocument } from "./types/PolicyAdmin.types";
import { Result } from "@badrap/result";

export class PolicyAdminService {
  private providers: Map<string, IPolicyAdminProvider> = new Map();

  constructor() {
    this.initializeProviders();
  }

  private initializeProviders(): void {
    const surefyreProvider = new SurefyreProvider({
      name: "surefyre",
      apiKey: config.n8n?.apiKey || "",
    });
    this.providers.set("surefyre", surefyreProvider);

    const riseProvider = new RISEProvider({
      name: "rise",
      apiKey: config.n8n?.apiKey || "",
    });
    this.providers.set("rise", riseProvider);
  }

  /**
   * Get application by ID using the appropriate provider
   * @param providerName - The provider name (e.g., 'surefyre', 'rise')
   * @param id - The application ID
   * @returns Promise resolving to the application data or error
   */
  async getApplicationById(
    providerName: string,
    id: string
  ): Promise<Result<Application, Error>> {
    const provider = this.getProvider(providerName);

    if (!provider) {
      return Result.err(new Error(`No provider found for: ${providerName}`));
    }

    return provider.getApplicationById(id);
  }

  /**
   * Get the provider for a specific name
   * @param providerName - The provider name
   * @returns The provider instance or undefined if not found
   */
  private getProvider(providerName: string): IPolicyAdminProvider | undefined {
    return this.providers.get(providerName.toLowerCase());
  }

  /**
   * Get all available providers
   * @returns Array of provider names and their provider names
   */
  async getAvailableProviders(): Promise<
    { name: string; providerName: string }[]
  > {
    const results = [];

    const entries = Array.from(this.providers.entries());
    for (const [key, provider] of entries) {
      results.push({
        name: key,
        providerName: provider.getProviderName(),
      });
    }

    return results;
  }

  /**
   * Register a new provider
   * @param key - The key to identify the provider
   * @param provider - The provider instance
   */
  registerProvider(key: string, provider: IPolicyAdminProvider): void {
    this.providers.set(key.toLowerCase(), provider);
  }

  /**
   * Get documents for an object using the appropriate provider
   * @param providerName - The provider name (e.g., 'surefyre', 'rise')
   * @param objectId - The object ID to get documents for
   * @returns Promise resolving to the documents data
   */
  async getDocuments(
    providerName: string,
    objectId: string
  ): Promise<Result<ApplicationDocument[], Error>> {
    const provider = this.getProvider(providerName);

    if (!provider) {
      return Result.err(new Error(`No provider found for: ${providerName}`));
    }

    if (!provider.getDocuments) {
      return Result.err(
        new Error(`Provider ${providerName} does not support getDocuments`)
      );
    }

    return provider.getDocuments(objectId);
  }

  /**
   * Check if a provider exists for the given name
   * @param providerName - The provider name
   * @returns True if provider exists
   */
  hasProvider(providerName: string): boolean {
    return this.providers.has(providerName.toLowerCase());
  }
}

// Create a singleton instance
export const policyAdminService = new PolicyAdminService();
